// src/components/TermsModal.js - 이용약관 모달
import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { Button } from "./ui/button";

const TermsModal = ({ isOpen, onClose, onAgree }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [canAgree, setCanAgree] = useState(false);
  const scrollContainerRef = useRef(null);
  const startTimeRef = useRef(null);

  // 모달이 열릴 때 시간 추적 시작
  useEffect(() => {
    if (isOpen) {
      startTimeRef.current = Date.now();
      setScrollProgress(0);
      setHasScrolledToBottom(false);
      setTimeElapsed(0);
      setCanAgree(false);

      // 1초마다 경과 시간 업데이트
      const timer = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setTimeElapsed(elapsed);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isOpen]);

  // 동의 버튼 활성화 조건 체크
  useEffect(() => {
    // 조건: 3초 이상 경과 AND 80% 이상 스크롤
    if (timeElapsed >= 3 && scrollProgress >= 80) {
      setCanAgree(true);
    }
  }, [timeElapsed, scrollProgress]);

  // 스크롤 이벤트 핸들러
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

    setScrollProgress(Math.min(Math.round(scrollPercentage), 100));

    // 90% 이상 스크롤하면 "맨 아래까지 읽음" 처리
    if (scrollPercentage >= 90) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAgree = () => {
    if (canAgree) {
      onAgree();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">이용약관</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">블루북 English 서비스 이용약관</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* 읽기 진행 상태 표시 */}
        <div className="px-3 sm:px-6 py-2 sm:py-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center justify-between text-xs sm:text-sm mb-2 gap-2">
            <span className="text-gray-700 whitespace-nowrap">
              {canAgree ? (
                <span className="text-green-600 font-semibold">✅ 동의 가능</span>
              ) : (
                <span className="text-gray-600">
                  ⏱️ {timeElapsed}초/3초
                </span>
              )}
            </span>
            <span className="text-gray-700 whitespace-nowrap">
              📜 {scrollProgress}% {hasScrolledToBottom && '✅'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                scrollProgress >= 80 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${scrollProgress}%` }}
            ></div>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
            💡 약관을 끝까지 읽어주세요 (80% 스크롤 + 3초 경과)
          </p>
        </div>

        {/* 요약 정보 */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-yellow-50 border-b border-yellow-200">
          <h3 className="text-xs sm:text-sm font-bold text-yellow-900 mb-2">📌 핵심 요약</h3>
          <ul className="text-[10px] sm:text-xs text-yellow-800 space-y-1">
            <li>• 회원가입 시 10건의 무료 다운로드가 제공됩니다.</li>
            <li>• 구독 시작일로부터 7일 이내 미사용 시 전액 환불 가능합니다.</li>
            <li>• 서비스 이용 후에는 일할 계산하여 환불됩니다.</li>
            <li>• 모든 콘텐츠의 저작권은 블루북 English에 있습니다.</li>
            <li>• 부정 사용 시 서비스 이용이 제한될 수 있습니다.</li>
          </ul>
        </div>

        {/* 약관 내용 (스크롤 가능) */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6"
        >
          <div className="prose prose-sm max-w-none">
            {/* 제1조 (목적) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제1조 (목적)</h3>
              <p className="text-gray-700 leading-relaxed">
                본 약관은 블루북 English(이하 "회사")가 제공하는 영어 학습 자료 구독 서비스(이하 "서비스")의 이용과 관련된 권리와 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
              </p>
            </section>

            {/* 제2조 (정의) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제2조 (정의)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> "서비스"란 회사가 제공하는 영어 학습 자료(PDF, 기타 디지털 콘텐츠)의 다운로드 및 이용 서비스를 의미합니다.</p>
                <p><span className="font-semibold">2.</span> "이용자"란 본 약관에 따라 회사가 제공하는 서비스를 받는 회원 및 비회원을 말합니다.</p>
                <p><span className="font-semibold">3.</span> "회원"이란 회사와 이용계약을 체결하고 회원 아이디(ID)를 부여받은 자를 말합니다.</p>
                <p><span className="font-semibold">4.</span> "구독"이란 회원이 일정 기간 동안 서비스를 이용할 수 있는 권한을 구매함을 의미합니다.</p>
              </div>
            </section>

            {/* 제3조 (약관의 효력 및 변경) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제3조 (약관의 효력 및 변경)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 본 약관은 서비스 화면에 게시하거나 기타의 방법으로 공지함으로써 효력이 발생합니다.</p>
                <p><span className="font-semibold">2.</span> 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 본 약관을 변경할 수 있습니다.</p>
                <p><span className="font-semibold">3.</span> 약관이 변경되는 경우 회사는 변경사항을 시행일자 7일 전부터 서비스 내 공지사항을 통해 공지합니다.</p>
                <p><span className="font-semibold">4.</span> 회원이 변경된 약관의 효력 발생일 이후에도 서비스를 계속 이용할 경우 변경된 약관에 동의한 것으로 간주합니다.</p>
              </div>
            </section>

            {/* 제4조 (회원가입) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제4조 (회원가입)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 이용자는 회사가 정한 가입 양식에 따라 회원정보를 기입한 후 본 약관에 동의한다는 의사표시를 함으로써 회원가입을 신청합니다.</p>
                <p><span className="font-semibold">2.</span> 회사는 제1항과 같이 회원으로 가입할 것을 신청한 이용자 중 다음 각 호에 해당하지 않는 한 회원으로 등록합니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>가입신청자가 본 약관에 의하여 이전에 회원자격을 상실한 적이 있는 경우</li>
                  <li>실명이 아니거나 타인의 명의를 이용한 경우</li>
                  <li>허위의 정보를 기재하거나, 회사가 제시하는 내용을 기재하지 않은 경우</li>
                  <li>기타 회원으로 등록하는 것이 회사의 기술상 현저히 지장이 있다고 판단되는 경우</li>
                </ul>
              </div>
            </section>

            {/* 제5조 (회원 탈퇴 및 자격 상실) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제5조 (회원 탈퇴 및 자격 상실)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회원은 언제든지 회사에 탈퇴를 요청할 수 있으며, 회사는 즉시 회원 탈퇴를 처리합니다.</p>
                <p><span className="font-semibold">2.</span> 회원이 다음 각 호의 사유에 해당하는 경우, 회사는 회원자격을 제한 및 정지시킬 수 있습니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>가입 신청 시에 허위 내용을 등록한 경우</li>
                  <li>다른 사람의 서비스 이용을 방해하거나 그 정보를 도용하는 등 전자상거래 질서를 위협하는 경우</li>
                  <li>서비스를 이용하여 법령 또는 본 약관이 금지하거나 공서양속에 반하는 행위를 하는 경우</li>
                </ul>
              </div>
            </section>

            {/* 제6조 (서비스의 제공 및 변경) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제6조 (서비스의 제공 및 변경)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 회원에게 아래와 같은 서비스를 제공합니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>영어 학습 자료(PDF 등) 다운로드 서비스</li>
                  <li>구독 기반 무제한 다운로드 서비스</li>
                  <li>기타 회사가 추가 개발하거나 다른 회사와의 제휴계약 등을 통해 회원에게 제공하는 일체의 서비스</li>
                </ul>
                <p><span className="font-semibold">2.</span> 회사는 서비스의 내용을 변경할 경우 그 사유를 회원에게 통지합니다.</p>
              </div>
            </section>

            {/* 제7조 (서비스의 중단) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제7조 (서비스의 중단)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 컴퓨터 등 정보통신설비의 보수점검, 교체 및 고장, 통신의 두절 등의 사유가 발생한 경우에는 서비스의 제공을 일시적으로 중단할 수 있습니다.</p>
                <p><span className="font-semibold">2.</span> 제1항에 의한 서비스 중단의 경우에는 회사는 사전 또는 사후에 이를 공지합니다.</p>
              </div>
            </section>

            {/* 제8조 (구독 및 결제) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제8조 (구독 및 결제)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 구독 서비스는 월 단위 또는 회사가 정한 기간 단위로 제공됩니다.</p>
                <p><span className="font-semibold">2.</span> 회원은 신용카드, 계좌이체 등 회사가 제공하는 결제 수단을 통해 구독료를 지불할 수 있습니다.</p>
                <p><span className="font-semibold">3.</span> 구독료는 구독 시작 시점에 선불로 청구되며, 자동 갱신될 수 있습니다.</p>
                <p><span className="font-semibold">4.</span> 회원가입 시 10건의 무료 다운로드 권한이 자동으로 제공됩니다.</p>
              </div>
            </section>

            {/* 제9조 (환불 정책) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제9조 (환불 정책)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회원은 구독 시작일로부터 7일 이내에 서비스를 이용하지 않은 경우 전액 환불을 요청할 수 있습니다.</p>
                <p><span className="font-semibold">2.</span> 서비스 이용 후에는 다음과 같이 일할 계산하여 환불합니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>환불금액 = 결제금액 - (결제금액 ÷ 구독일수 × 이용일수)</li>
                </ul>
                <p><span className="font-semibold">3.</span> 환불은 회원의 요청일로부터 영업일 기준 3-5일 이내에 처리됩니다.</p>
                <p><span className="font-semibold">4.</span> 다음의 경우 환불이 제한될 수 있습니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>회원의 귀책사유로 서비스 이용이 불가능한 경우</li>
                  <li>약관 위반으로 인해 이용이 정지된 경우</li>
                </ul>
              </div>
            </section>

            {/* 제10조 (저작권 및 지적재산권) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제10조 (저작권 및 지적재산권)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사가 제공하는 모든 콘텐츠(PDF 자료, 이미지, 텍스트 등)에 대한 저작권 및 지적재산권은 회사에 귀속됩니다.</p>
                <p><span className="font-semibold">2.</span> 회원은 서비스를 이용함으로써 얻은 정보를 회사의 사전 승낙 없이 복제, 송신, 출판, 배포, 방송 기타 방법에 의하여 영리목적으로 이용하거나 제3자에게 이용하게 하여서는 안됩니다.</p>
                <p><span className="font-semibold">3.</span> 회원은 개인적인 학습 목적으로만 다운로드한 자료를 사용할 수 있으며, 상업적 목적으로 사용할 수 없습니다.</p>
              </div>
            </section>

            {/* 제11조 (회원의 의무) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제11조 (회원의 의무)</h3>
              <div className="space-y-2 text-gray-700">
                <p>회원은 다음 행위를 하여서는 안됩니다:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>신청 또는 변경 시 허위 내용의 등록</li>
                  <li>타인의 정보 도용</li>
                  <li>회사가 게시한 정보의 변경</li>
                  <li>회사가 정한 정보 이외의 정보(컴퓨터 프로그램 등) 등의 송신 또는 게시</li>
                  <li>회사와 기타 제3자의 저작권 등 지적재산권에 대한 침해</li>
                  <li>회사 및 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</li>
                  <li>외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 서비스에 공개 또는 게시하는 행위</li>
                </ul>
              </div>
            </section>

            {/* 제12조 (면책조항) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제12조 (면책조항)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.</p>
                <p><span className="font-semibold">2.</span> 회사는 회원의 귀책사유로 인한 서비스 이용의 장애에 대하여 책임을 지지 않습니다.</p>
                <p><span className="font-semibold">3.</span> 회사는 회원이 서비스를 이용하여 기대하는 학습 효과나 결과를 얻지 못한 것에 대하여 책임을 지지 않습니다.</p>
              </div>
            </section>

            {/* 제13조 (분쟁해결) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제13조 (분쟁해결)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 회사는 회원이 제기하는 정당한 의견이나 불만을 반영하고 그 피해를 보상처리하기 위하여 피해보상처리기구를 설치·운영합니다.</p>
                <p><span className="font-semibold">2.</span> 회사는 회원으로부터 제출되는 불만사항 및 의견은 우선적으로 그 사항을 처리합니다. 다만, 신속한 처리가 곤란한 경우에는 회원에게 그 사유와 처리일정을 즉시 통보합니다.</p>
              </div>
            </section>

            {/* 제14조 (재판권 및 준거법) */}
            <section className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">제14조 (재판권 및 준거법)</h3>
              <div className="space-y-2 text-gray-700">
                <p><span className="font-semibold">1.</span> 본 약관은 대한민국 법률에 따라 규율되고 해석됩니다.</p>
                <p><span className="font-semibold">2.</span> 회사와 회원 간 발생한 분쟁에 관한 소송은 서울중앙지방법원을 전속 관할법원으로 합니다.</p>
              </div>
            </section>

            {/* 부칙 */}
            <section className="mb-6 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-3">부칙</h3>
              <p className="text-gray-700">본 약관은 2025년 1월 1일부터 시행합니다.</p>
            </section>

            {/* 문의 정보 */}
            <section className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-bold text-gray-900 mb-2">📞 문의하기</h3>
              <p className="text-sm text-gray-700">
                약관 관련 문의사항이 있으시면 아래로 연락주세요:
              </p>
              <p className="text-sm text-gray-700 mt-2">
                이메일: <a href="mailto:support@bluebookgo.com" className="text-blue-600 hover:underline">support@bluebookgo.com</a>
              </p>
            </section>

            {/* 스크롤 끝 표시 */}
            <div className="text-center py-4 text-gray-400 text-sm">
              - 약관의 끝 -
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-6 border-t border-gray-200 bg-gray-50 gap-2">
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            {!canAgree && (
              <span>💡 약관을 끝까지 읽어주세요</span>
            )}
          </div>
          <div className="flex gap-2 sm:gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none text-sm"
            >
              닫기
            </Button>
            <Button
              onClick={handleAgree}
              disabled={!canAgree}
              className={`flex-1 sm:flex-none sm:min-w-[120px] text-sm ${
                !canAgree ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {canAgree ? '✅ 동의합니다' : '🔒 읽는 중...'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
